import { useTranslation } from "react-i18next";

import { useHasKey } from "../components/useHasKey";

/**
 * Four claims, each one traceable to a row in landing/todo/10-claims-matrix.md — and each
 * one printed with the thing that enforces it.
 *
 * `gate` is the name of the CI check that fails the build if the claim above it stops being
 * true, or the library the claim rests on. These are IDENTIFIERS, not prose: a filename does
 * not get translated, so it lives here rather than in the dictionary, and it is the detail
 * that turns "we promise" into "go and look". The mockup prints them for the same reason.
 */
const PILLARS = [
  { key: "server", gate: "check-proto-content-boundary.mjs" },
  { key: "identifiers", gate: "check-forbidden-columns.mjs" },
  { key: "protocol", gate: "libsignal · PQXDH · Kyber-1024" },
  { key: "calls", gate: "check-call-media-key-boundary.mjs" },
] as const;

export function Pillars() {
  const { t } = useTranslation();
  const has = useHasKey();

  const pillars = PILLARS.filter((pillar) => has(`pillars.${pillar.key}.title`));

  if (pillars.length === 0) return null;

  return (
    <section className="section" id="pillars">
      <div className="section__head">
        {/* A numeral, not a label: it orders the page in every locale without a string. */}
        <span className="section__eyebrow">01</span>
        <h2 className="section__heading">{t("pillars.heading")}</h2>
      </div>

      <ul className="grid grid--4">
        {pillars.map((pillar, index) => (
          <li className="card" key={pillar.key}>
            {/* Numbered from the rendered list, so dropping a claim renumbers the rest
                rather than leaving a gap. */}
            <span className="card__num">{String(index + 1).padStart(2, "0")}</span>
            <h3 className="card__title">{t(`pillars.${pillar.key}.title`)}</h3>
            <p className="card__body">{t(`pillars.${pillar.key}.body`)}</p>
            <span className="card__gate">{pillar.gate}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
